import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Body } from '@components/dashboard/Typography';
import Colors from '@components/shared/Colors';
import LazyLoadImage from '@components/shared/LazyLoadImage';
import { EXTERNAL_LINK_SVG, BREAKPOINT } from '@constants';
import { PageSlice, SliceImageAndInfoDetails } from '@contracts';
import handleTextReplacement from '@utils/handleTextReplacement';
import { getTranslatedHtml } from '@utils/html';
import Badge from './Badge';
import { TextVariant, Typography } from './design/Typography';
import LinkButton from './LinkButton';
import LinkTranslated from './LinkTranslated';
import TwoColumn from './TwoColumn';

interface Props {
  slice: PageSlice;
  usedInDashboard?: boolean;
}

const ImageAndInfo = ({ slice, usedInDashboard }: Props) => {
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SliceImageAndInfoDetails;
  const paddedStyle = sliceDetails.desktop_padding ?? '0';
  const breakpoint = usedInDashboard ? `${BREAKPOINT.dashboardPage}px` : `${BREAKPOINT.laptopM}px`;

  return (
    <Wrapper $padding={paddedStyle}>
      <Inner $mobileBackground={sliceDetails?.mobile_background_style} $background={sliceDetails?.background_style}>
        <TwoColumn
          breakpoint={breakpoint}
          equal
          layout={sliceDetails.layout}
          desktopReverse={sliceDetails.layout === 'text-image'}
          mobileReverse={sliceDetails.mobile_layout === 'text-image'}
        >
          <ImageWrapper $breakpoint={breakpoint}>
            <LazyLoadImage src={sliceDetails?.image?.url} alt={sliceDetails?.image?.alt || ''} />
          </ImageWrapper>
          <TextWrapper $breakpoint={breakpoint} $mobileAlignCenter={!!sliceDetails?.mobile_align_center}>
            {sliceDetails.aux_image?.url && (
              <AuxImageWrap $breakpoint={breakpoint}>
                <LazyLoadImage src={sliceDetails?.aux_image?.url} alt={sliceDetails?.image?.alt} />
              </AuxImageWrap>
            )}
            {sliceDetails.top_heading_text_key ? (
              <TopTitleWrapper $breakpoint={breakpoint}>
                {sliceDetails.top_heading_badge_text_key ? (
                  <BadgeWrapper>
                    <Badge type="plus" text={t(sliceDetails.top_heading_badge_text_key)} />
                  </BadgeWrapper>
                ) : null}
                <TopTitleTextWrapper>{t(sliceDetails.top_heading_text_key)}</TopTitleTextWrapper>
              </TopTitleWrapper>
            ) : null}
            <TitleWrapper
              $breakpoint={breakpoint}
              $fontSize={sliceDetails?.heading_font_size}
              $fontSizeMobile={sliceDetails?.heading_font_size_mobile}
            >
              {t(sliceDetails.heading_text_key || '')}
            </TitleWrapper>
            {sliceDetails.description_text_key ? (
              <DescWrapper $padding={paddedStyle}>
                <Body>{getTranslatedHtml(t(sliceDetails.description_text_key || ''))}</Body>
              </DescWrapper>
            ) : null}
            {sliceDetails.focus_text_key ? (
              <FocusWrapper>
                <Typography $variant={TextVariant.Heading1} $mobileVariant={TextVariant.Heading3} $color={Colors.gold}>
                  {handleTextReplacement(t(sliceDetails.focus_text_key))}
                </Typography>
              </FocusWrapper>
            ) : null}
            {sliceDetails.focus_text_description_key ? (
              <FocusDescWrapper $breakpoint={breakpoint}>
                <Typography $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
                  {handleTextReplacement(t(sliceDetails.focus_text_description_key))}
                </Typography>
              </FocusDescWrapper>
            ) : null}
            {sliceDetails?.link && (
              <ButtonWrapper $breakpoint={breakpoint}>
                {sliceDetails?.show_link_instead_of_button ? (
                  <LinkWrapper>
                    <LinkTranslated to={sliceDetails.link || ''} targetBlank>
                      {t(sliceDetails.button_text_key || 'components.source')}
                    </LinkTranslated>
                  </LinkWrapper>
                ) : (
                  <LinkButton borderRadius="5px" to={sliceDetails.link || ''}>
                    {t(sliceDetails.button_text_key || '')}
                  </LinkButton>
                )}
              </ButtonWrapper>
            )}
          </TextWrapper>
        </TwoColumn>
      </Inner>
    </Wrapper>
  );
};

export default ImageAndInfo;

type StyleProps = {
  $padding?: string;
  $breakpoint?: string;
  $mobileBackground?: string;
  $background?: string;
  $mobileAlignCenter?: boolean;
  $fontSize?: string;
  $fontSizeMobile?: string;
};

const Wrapper = styled.div<StyleProps>`
  max-width: ${(props) => (props.$padding === '0' ? '2000px' : '100%')};
  margin: 0 auto;
  padding: ${(props) => props.$padding || '0'};

  @media (max-width: ${(props) => props.$breakpoint}) {
    padding: 0;
  }
`;

const Inner = styled.div<StyleProps>`
  background: ${(props) => props.$background || Colors.babyBlue};
  border-radius: 2px;

  @media (max-width: ${(props) => props.$breakpoint}) {
    background: ${(props) => props.$mobileBackground || 'none'};
  }
`;

const TextWrapper = styled.div<StyleProps>`
  color: ${Colors.navyBlue};
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  max-width: 50%;
  width: 100%;
  padding: 30px 70px;

  > * {
    max-width: 480px;
  }

  @media (max-width: ${(props) => props.$breakpoint}) {
    padding: 30px 25px;
    max-width: 100%;
    min-height: 350px;
    justify-content: center;

    * {
      ${(props) => (props.$mobileAlignCenter ? 'text-align: center !important;' : null)}
    }
  }
`;

const BadgeWrapper = styled.div``;

const AuxImageWrap = styled.div<StyleProps>`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 8px;

  > svg {
    width: 64px;
  }

  > img {
    width: 64px;
  }

  @media (max-width: ${(props) => props.$breakpoint}) {
    align-items: center;
    justify-content: center;
  }
`;

const TitleWrapper = styled.p<StyleProps>`
  color: ${Colors.navyBlue};
  margin-bottom: 24px;
  font-family: Domaine;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.4px;
  text-align: left;
  width: 100%;
  font-size: ${(props) => props.$fontSize || '40px'};

  @media (max-width: ${(props) => props.$breakpoint}) {
    font-size: ${(props) => props.$fontSizeMobile || '32px'};
  }
`;

const DescWrapper = styled.div<{ $padding: string }>`
  margin-bottom: 24px;
  max-width: 480px;
  text-align: left;
  width: 100%;

  & > p {
    font-size: ${(props) => (props.$padding ? '14px' : '16px')};
  }
`;

const TopTitleWrapper = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  width: 100%;
  max-width: 480px;

  @media (max-width: ${(props) => props.$breakpoint}) {
    justify-content: center;
  }
`;

const LinkWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;

  a {
    font-family: BrownStd;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 140%; /* 19.6px */
    color: ${Colors.lilac};

    :hover {
      text-decoration: underline;
    }

    &:after {
      content: '';
      background-image: url(${EXTERNAL_LINK_SVG});
      background-repeat: no-repeat;
      background-position: center;
      width: 15px;
      height: 15px;
      display: inline-block;
      margin-left: 5px;
      vertical-align: -2px;
    }
  }
`;

const FocusWrapper = styled.div`
  width: 100%;
  margin-bottom: 8px;
`;

const FocusDescWrapper = styled.div<StyleProps>`
  width: 100%;
  margin-bottom: 8px;

  p {
    max-width: 320px;
  }

  @media (max-width: ${(props) => props.$breakpoint}) {
    max-width: 300px;

    p {
      max-width: auto;
    }
  }
`;

const TopTitleTextWrapper = styled.div`
  color: ${Colors.navyBlue};
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding-right: 20px;
`;

const ButtonWrapper = styled.div<StyleProps>`
  display: flex;
  width: 100%;
  max-width: 480px;

  @media (max-width: ${(props) => props.$breakpoint}) {
    a {
      width: 100%;
      border-radius: 2px;
    }
  }
`;

const ImageWrapper = styled.div<StyleProps>`
  width: 100%;
  max-width: 50%;
  flex-grow: 1;

  img {
    width: 100%;
  }

  @media (max-width: ${(props) => props.$breakpoint}) {
    overflow: hidden;
    max-width: 480px;
  }
`;
